import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, ColCountByScreen, Label } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import { useSelector } from 'react-redux'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import '../../configuration.css'
import { PatternRule } from 'devextreme-react/data-grid'
import { IconButton, Stack, useMediaQuery, Box, Alert, Button } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const DESIRED_IMAGE_WIDTH = 750
const DESIRED_IMAGE_HEIGHT = 430
const TARGET_ASPECT_RATIO = DESIRED_IMAGE_WIDTH / DESIRED_IMAGE_HEIGHT
const ASPECT_RATIO_TOLERANCE = 0.02

const UpdateBannerBarForm = (props) => {
    const navigate = useNavigate()
    const { handleSubmit } = useForm()
    const { loading } = useSelector((state) => state.banners)
    const [labelLocation, setLabelLocation] = useState('left')
    const [imageLoadFailed, setImageLoadFailed] = useState(false)
    const [replacementFile, setReplacementFile] = useState(null)
    const [replacementPreviewUrl, setReplacementPreviewUrl] = useState(null)
    const [replacementDimensionError, setReplacementDimensionError] = useState(null)
    const sm = useMediaQuery('(max-width:600px)')
    const hasExistingImage = Boolean(props.bannerBarData?.filePath)
    const showInvalidImageActions = hasExistingImage && imageLoadFailed
    const previewSrc = replacementPreviewUrl || (hasExistingImage ? props.bannerBarData?.filePath : null)

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    useEffect(() => {
        if (!hasExistingImage) setImageLoadFailed(false)
    }, [hasExistingImage])

    useEffect(() => {
        if (!replacementFile) {
            setReplacementPreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev)
                return null
            })
            setReplacementDimensionError(null)
            return
        }
        const url = URL.createObjectURL(replacementFile)
        setReplacementPreviewUrl(url)
        setReplacementDimensionError(null)
        const img = new Image()
        img.onload = () => {
            const w = img.naturalWidth
            const h = img.naturalHeight
            const actualRatio = h > 0 ? w / h : 0
            const ratioError = Math.abs(actualRatio - TARGET_ASPECT_RATIO) / TARGET_ASPECT_RATIO
            const ok = ratioError <= ASPECT_RATIO_TOLERANCE
            setReplacementDimensionError(
                ok
                    ? null
                    : `Image aspect ratio should match ${DESIRED_IMAGE_WIDTH} × ${DESIRED_IMAGE_HEIGHT} (width:height). Selected image is ${w} × ${h} px.`
            )
        }
        img.onerror = () => setReplacementDimensionError('Could not read image dimensions.')
        img.src = url
        return () => URL.revokeObjectURL(url)
    }, [replacementFile])

    const onSubmitForm = (e) => {
        e?.preventDefault?.()
        if (replacementFile && replacementDimensionError) return
        if (replacementFile && props.onReplaceImage) {
            props.onReplaceImage(props.bannerBarData, replacementFile)
        } else {
            props.updateBannerBar()
        }
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/settings/configuration/banner-bar', {
                state: { navigateFrom: 'cancel-update-banner-bar' },
            }),
    }

    const validationRules = {
        dateStart: [{ type: 'required', message: 'Date Start is required.' }],
        dateEnd: [{ type: 'required', message: 'Date End is required.' }],
    }

    const editorOptionsView = {
        readOnly: true,
    }

    const navigationLinkEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }

    const navigationLinkPattern = /^(?=.{16,})https:\/\/.*$/

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/settings/configuration/banner-bar`, {
                                    state: { navigateFrom: 'cancel-update-banner-bar' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Update Banner Bar
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form onSubmit={onSubmitForm}>
                    <Form
                        mode='form'
                        formData={props.bannerBarData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='Update Banner Bar' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem dataField='isActive' editorType='dxCheckBox'>
                                    <Label text='Enabled' />
                                </SimpleItem>
                                <SimpleItem dataField='subText'>
                                    <Label text='Sub Text' />
                                </SimpleItem>
                                <SimpleItem dataField='navigationLink' editorOptions={navigationLinkEditorOptions}>
                                    <Label text='Navigation Link' />
                                    <PatternRule
                                        message='navigation link must start with https:// and be at least 8 characters'
                                        pattern={navigationLinkPattern}
                                    />
                                </SimpleItem>
                                <SimpleItem dataField='orderBy'>
                                    <Label text='Sort Order' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='dateStart'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateStart}
                                    editorOptions={{
                                        displayFormat: 'shortDateShortTime',
                                        type: 'datetime',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem
                                    dataField='dateEnd'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateEnd}
                                    editorOptions={{
                                        displayFormat: 'shortDateShortTime',
                                        type: 'datetime',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem>
                                    <Box className='image-container' sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                            <Alert
                                                severity='info'
                                                icon={<InfoOutlinedIcon fontSize='small' />}
                                                sx={{
                                                    py: 0.5,
                                                    maxWidth: 280,
                                                    '& .MuiAlert-message': { fontSize: '0.8125rem' },
                                                }}>
                                                Recommended image aspect ratio: {DESIRED_IMAGE_WIDTH} × {DESIRED_IMAGE_HEIGHT} (width × height). Max file size 256 KB.
                                            </Alert>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                            {previewSrc ? (
                                                <>
                                                    <img
                                                        src={previewSrc}
                                                        alt='Preview'
                                                        className='image-preview'
                                                        style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                                                        onError={() => !replacementFile && setImageLoadFailed(true)}
                                                        onLoad={() => !replacementFile && setImageLoadFailed(false)}
                                                    />
                                                    {showInvalidImageActions && !replacementFile && (
                                                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                            <Typography variant='body2' color='text.secondary'>
                                                                Previous image is unavailable. Choose a new image and click Save to update.
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant='body2' color='text.secondary'>No image</Typography>
                                            )}
                                        </Box>
                                        {replacementDimensionError && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                                <Alert
                                                    severity='warning'
                                                    sx={{
                                                        maxWidth: 280,
                                                        '& .MuiAlert-message': { fontSize: '0.8125rem' },
                                                    }}>
                                                    {replacementDimensionError}
                                                </Alert>
                                            </Box>
                                        )}
                                        {props.onReplaceImage && (hasExistingImage || replacementFile) && (
                                            <Stack
                                                direction='row'
                                                spacing={1}
                                                flexWrap='wrap'
                                                useFlexGap
                                                alignItems='center'
                                                justifyContent='flex-end'
                                                sx={{ mt: 0.5 }}>
                                                <Button
                                                    size='small'
                                                    variant='outlined'
                                                    component='label'
                                                    startIcon={<CloudUploadIcon />}
                                                    disabled={loading}>
                                                    Choose new image
                                                    <input
                                                        type='file'
                                                        accept='.jpg,.jpeg,.png,image/*'
                                                        hidden
                                                        onChange={(ev) => {
                                                            const f = ev.target.files?.[0]
                                                            setReplacementFile(f || null)
                                                        }}
                                                    />
                                                </Button>
                                                {replacementFile && !replacementDimensionError && (
                                                    <Typography variant='caption' color='text.secondary'>
                                                        {replacementFile.name} — click Save to update
                                                    </Typography>
                                                )}
                                            </Stack>
                                        )}
                                    </Box>
                                </SimpleItem>
                                <SimpleItem dataField='fileName' editorOptions={editorOptionsView}>
                                    <Label text='File Name' />
                                </SimpleItem>
                                <SimpleItem dataField='fileSize' editorOptions={editorOptionsView}>
                                    <Label text='Size' />
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading || Boolean(replacementFile && replacementDimensionError),
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default UpdateBannerBarForm
